// Themed category colors - each theme has a full color spectrum adjusted to its vibe
// Categories: trips, experiences, friends, sports, work, family, health, learning, romance, creativity, food, music

// Default category colors (vibrant rainbow spectrum)
export const defaultCategoryColors = {
    trips: '#FF9F43',      // Orange
    experiences: '#EE5A5A', // Red
    friends: '#26DE81',     // Green
    sports: '#F7D731',      // Yellow
    work: '#5F6DBA',        // Indigo
    family: '#FF6B9D',      // Pink
    health: '#20BF6B',      // Teal green
    learning: '#4B7BEC',    // Blue
    romance: '#FC5C65',     // Rose red
    creativity: '#A55EEA',  // Purple
    food: '#FD9644',        // Orange-peach
    music: '#45AAF2'        // Sky blue
};

// Theme-specific category colors - full spectrum adjusted for each theme's vibe
export const themeCategoryColors = {
    // === CLASSIC - Clean, professional colors ===
    basic: {
        trips: '#E67E22',
        experiences: '#C0392B',
        friends: '#27AE60',
        sports: '#F1C40F',
        work: '#34495E',
        family: '#E91E63',
        health: '#16A085',
        learning: '#2980B9',
        romance: '#E74C3C',
        creativity: '#9B59B6',
        food: '#D35400',
        music: '#3498DB'
    },
    executive: {
        trips: '#B8860B',
        experiences: '#8B0000',
        friends: '#2E8B57',
        sports: '#BDB76B',
        work: '#2F4F4F',
        family: '#C71585',
        health: '#008B8B',
        learning: '#4169E1',
        romance: '#DC143C',
        creativity: '#6A5ACD',
        food: '#CD853F',
        music: '#4682B4'
    },
    newspaper: {
        trips: '#A0522D',
        experiences: '#8B4513',
        friends: '#556B2F',
        sports: '#B8860B',
        work: '#2F4F4F',
        family: '#8B008B',
        health: '#2E8B57',
        learning: '#483D8B',
        romance: '#800000',
        creativity: '#4B0082',
        food: '#D2691E',
        music: '#5F9EA0'
    },

    // === NATURE - Earthy, organic feel ===
    grownups: {
        trips: '#E8A87C',
        experiences: '#E27D60',
        friends: '#41B3A3',
        sports: '#C5D86D',
        work: '#5B8A72',
        family: '#E8B4BC',
        health: '#2D6A4F',
        learning: '#5AA897',
        romance: '#E07A5F',
        creativity: '#81B29A',
        food: '#F2CC8F',
        music: '#3D9B8F'
    },
    forest: {
        trips: '#D4A373',
        experiences: '#BC6C25',
        friends: '#588157',
        sports: '#A7C957',
        work: '#344E41',
        family: '#D4A5A5',
        health: '#3A5A40',
        learning: '#606C38',
        romance: '#9B2335',
        creativity: '#7B68EE',
        food: '#DDA15E',
        music: '#457B9D'
    },
    grass: {
        trips: '#F4A261',
        experiences: '#E76F51',
        friends: '#2A9D8F',
        sports: '#E9C46A',
        work: '#264653',
        family: '#F4ACB7',
        health: '#52B788',
        learning: '#168AAD',
        romance: '#D62828',
        creativity: '#7209B7',
        food: '#F8961E',
        music: '#4CC9F0'
    },
    matcha: {
        trips: '#C9ADA7',
        experiences: '#9A8C98',
        friends: '#4A7C59',
        sports: '#B5C99A',
        work: '#4F6367',
        family: '#D4A5A5',
        health: '#6B9080',
        learning: '#5C6B73',
        romance: '#B56576',
        creativity: '#8E7DBE',
        food: '#C9B79C',
        music: '#7EB09B'
    },
    cottagecore: {
        trips: '#D4A373',
        experiences: '#A98467',
        friends: '#7C9473',
        sports: '#D9C5A0',
        work: '#6B5B4F',
        family: '#D1A7A0',
        health: '#87A96B',
        learning: '#8B7355',
        romance: '#C08081',
        creativity: '#A6819B',
        food: '#C19A6B',
        music: '#8DA399'
    },
    goblincore: {
        trips: '#8B7355',
        experiences: '#7C4C3E',
        friends: '#556B2F',
        sports: '#9B9B4D',
        work: '#3D3D29',
        family: '#8B6F6F',
        health: '#4A5D23',
        learning: '#5C4033',
        romance: '#7D4E57',
        creativity: '#5D4E6D',
        food: '#997950',
        music: '#4E6E5D'
    },

    // === OCEAN & SKY - Cool, serene palette ===
    sea: {
        trips: '#F4A460',
        experiences: '#E55B3C',
        friends: '#20B2AA',
        sports: '#FFE66D',
        work: '#1E3D59',
        family: '#F08080',
        health: '#48D1CC',
        learning: '#4682B4',
        romance: '#E9546B',
        creativity: '#9370DB',
        food: '#FFA07A',
        music: '#00CED1'
    },
    sky: {
        trips: '#FFB347',
        experiences: '#FF6B6B',
        friends: '#77DD77',
        sports: '#FDFD96',
        work: '#6A5ACD',
        family: '#FFB6C1',
        health: '#40E0D0',
        learning: '#6495ED',
        romance: '#FF69B4',
        creativity: '#DDA0DD',
        food: '#FFDAB9',
        music: '#87CEEB'
    },

    // === WARM TONES - Cozy, warm spectrum ===
    pink: {
        trips: '#FFB347',
        experiences: '#FF6B6B',
        friends: '#98D8C8',
        sports: '#FFE66D',
        work: '#B19CD9',
        family: '#FF69B4',
        health: '#7FCDCD',
        learning: '#779ECB',
        romance: '#FF1493',
        creativity: '#DA70D6',
        food: '#FFDAB9',
        music: '#89CFF0'
    },
    roseGold: {
        trips: '#E6A57E',
        experiences: '#C97064',
        friends: '#8AAE92',
        sports: '#D4AF37',
        work: '#8B7D82',
        family: '#E8B4BC',
        health: '#7BA38B',
        learning: '#7393B3',
        romance: '#E75480',
        creativity: '#AA7DA6',
        food: '#D4A574',
        music: '#A4C8E1'
    },
    blood: {
        trips: '#FF7F50',
        experiences: '#DC143C',
        friends: '#00CED1',
        sports: '#FFD700',
        work: '#4A0404',
        family: '#FF69B4',
        health: '#20B2AA',
        learning: '#4169E1',
        romance: '#FF0000',
        creativity: '#8B008B',
        food: '#FFA500',
        music: '#1E90FF'
    },
    orange: {
        trips: '#FF8C00',
        experiences: '#FF6347',
        friends: '#3CB371',
        sports: '#FFD700',
        work: '#8B4513',
        family: '#FF7F7F',
        health: '#2E8B57',
        learning: '#4169E1',
        romance: '#FF4500',
        creativity: '#BA55D3',
        food: '#FFA500',
        music: '#00BFFF'
    },
    gold: {
        trips: '#DAA520',
        experiences: '#CD5C5C',
        friends: '#6B8E23',
        sports: '#FFD700',
        work: '#8B6914',
        family: '#DB7093',
        health: '#228B22',
        learning: '#4682B4',
        romance: '#DC143C',
        creativity: '#9932CC',
        food: '#F4A460',
        music: '#5F9EA0'
    },

    // === NEUTRALS - Muted but distinct ===
    brown: {
        trips: '#CD853F',
        experiences: '#A0522D',
        friends: '#6B8E23',
        sports: '#BDB76B',
        work: '#5C4033',
        family: '#BC8F8F',
        health: '#556B2F',
        learning: '#5F9EA0',
        romance: '#B22222',
        creativity: '#8B668B',
        food: '#D2691E',
        music: '#708090'
    },
    marronbeige: {
        trips: '#CC8066',
        experiences: '#A85C50',
        friends: '#7D9B76',
        sports: '#C9B97C',
        work: '#6B5352',
        family: '#D4A5A5',
        health: '#669980',
        learning: '#6B8BA4',
        romance: '#C4626E',
        creativity: '#9A7B9A',
        food: '#C4956A',
        music: '#84A9AC'
    },
    millennialBeige: {
        trips: '#C4956A',
        experiences: '#A67B5B',
        friends: '#81A086',
        sports: '#C9B887',
        work: '#6B5B4D',
        family: '#D4A5A5',
        health: '#7BA38B',
        learning: '#7393B3',
        romance: '#C4727E',
        creativity: '#9B8AA6',
        food: '#CDA776',
        music: '#8DA6B3'
    },

    // === PURPLE - Rich purple-based spectrum ===
    purple: {
        trips: '#FF9F43',
        experiences: '#EE5253',
        friends: '#26DE81',
        sports: '#FEE140',
        work: '#5758BB',
        family: '#FF6B81',
        health: '#1DD1A1',
        learning: '#54A0FF',
        romance: '#FF6B6B',
        creativity: '#A55EEA',
        food: '#FECA57',
        music: '#48DBFB'
    },

    // === DARK THEMES - Vibrant against dark ===
    strangerThings: {
        trips: '#FF6B35',
        experiences: '#FF0000',
        friends: '#00FF9F',
        sports: '#FFFF00',
        work: '#8B0000',
        family: '#FF69B4',
        health: '#00CED1',
        learning: '#4169E1',
        romance: '#DC143C',
        creativity: '#9400D3',
        food: '#FFA500',
        music: '#00BFFF'
    },
    matrix: {
        trips: '#39FF14',
        experiences: '#FF3131',
        friends: '#00FF00',
        sports: '#ADFF2F',
        work: '#006400',
        family: '#FF1493',
        health: '#00FA9A',
        learning: '#00CED1',
        romance: '#FF0000',
        creativity: '#BA55D3',
        food: '#7CFC00',
        music: '#00FFFF'
    },
    darkAcademia: {
        trips: '#C19A6B',
        experiences: '#8B4513',
        friends: '#556B2F',
        sports: '#BDB76B',
        work: '#3D2B1F',
        family: '#BC8F8F',
        health: '#6B8E23',
        learning: '#4A5568',
        romance: '#800000',
        creativity: '#614051',
        food: '#D2691E',
        music: '#708090'
    },
    tumblr2014: {
        trips: '#FF9F43',
        experiences: '#EB3B5A',
        friends: '#20BF6B',
        sports: '#F7B731',
        work: '#4B6584',
        family: '#FC5C65',
        health: '#26DE81',
        learning: '#4B7BEC',
        romance: '#EB3B5A',
        creativity: '#8854D0',
        food: '#FD9644',
        music: '#45AAF2'
    },
    ai2025: {
        trips: '#FF9F43',
        experiences: '#EB3B5A',
        friends: '#26DE81',
        sports: '#F7B731',
        work: '#7C3AED',
        family: '#EC4899',
        health: '#10B981',
        learning: '#3B82F6',
        romance: '#F43F5E',
        creativity: '#8B5CF6',
        food: '#F59E0B',
        music: '#06B6D4'
    },

    // === RETRO - Bold, nostalgic colors ===
    vaporwave: {
        trips: '#FF6AD5',
        experiences: '#FF71CE',
        friends: '#01CDFE',
        sports: '#FFFB96',
        work: '#B967FF',
        family: '#FF9FFD',
        health: '#05FFA1',
        learning: '#69DAFF',
        romance: '#FF71CE',
        creativity: '#C774E8',
        food: '#FFE66D',
        music: '#00FFFF'
    },
    windows95: {
        trips: '#FF8C00',
        experiences: '#FF0000',
        friends: '#008000',
        sports: '#FFFF00',
        work: '#000080',
        family: '#FF00FF',
        health: '#008080',
        learning: '#0000FF',
        romance: '#FF0000',
        creativity: '#800080',
        food: '#FFA500',
        music: '#00FFFF'
    },
    blog2006: {
        trips: '#FF9900',
        experiences: '#FF0066',
        friends: '#00FF00',
        sports: '#FFFF00',
        work: '#FF1493',
        family: '#FF69B4',
        health: '#00FFFF',
        learning: '#3399FF',
        romance: '#FF0066',
        creativity: '#FF00FF',
        food: '#FFCC00',
        music: '#00CCFF'
    },
    ascii80s: {
        trips: '#FF5500',
        experiences: '#FF0000',
        friends: '#00FF00',
        sports: '#FFFF00',
        work: '#00AA00',
        family: '#FF00FF',
        health: '#00FFAA',
        learning: '#0088FF',
        romance: '#FF0055',
        creativity: '#AA00FF',
        food: '#FFAA00',
        music: '#00FFFF'
    },
    windowsXP: {
        trips: '#FF9933',
        experiences: '#FF3333',
        friends: '#33CC33',
        sports: '#FFCC00',
        work: '#0054E3',
        family: '#FF66B2',
        health: '#00CC99',
        learning: '#3399FF',
        romance: '#FF3366',
        creativity: '#9966FF',
        food: '#FFAA33',
        music: '#33CCFF'
    },
    msPaint: {
        trips: '#FF8000',
        experiences: '#FF0000',
        friends: '#00FF00',
        sports: '#FFFF00',
        work: '#000080',
        family: '#FF00FF',
        health: '#00FFFF',
        learning: '#0000FF',
        romance: '#FF0080',
        creativity: '#8000FF',
        food: '#FFC000',
        music: '#00C0FF'
    },

    // === POP CULTURE ===
    brat: {
        trips: '#FF6B35',
        experiences: '#E83F3F',
        friends: '#8ACE00',
        sports: '#FFD93D',
        work: '#5A8800',
        family: '#FF69B4',
        health: '#7BB800',
        learning: '#4169E1',
        romance: '#FF3864',
        creativity: '#9B59B6',
        food: '#FF9F1C',
        music: '#00B4D8'
    },
    barbiecore: {
        trips: '#FFB347',
        experiences: '#FF6B6B',
        friends: '#98FB98',
        sports: '#FFD700',
        work: '#FF1493',
        family: '#FF69B4',
        health: '#7FFFD4',
        learning: '#87CEEB',
        romance: '#FF1493',
        creativity: '#DA70D6',
        food: '#FFB6C1',
        music: '#DDA0DD'
    },
    lisaFrank: {
        trips: '#FF6B35',
        experiences: '#FF1493',
        friends: '#00FF7F',
        sports: '#FFD700',
        work: '#9400D3',
        family: '#FF69B4',
        health: '#00FFFF',
        learning: '#4169E1',
        romance: '#FF1493',
        creativity: '#FF00FF',
        food: '#FFA500',
        music: '#00CED1'
    },
    wesAnderson: {
        trips: '#E8A87C',
        experiences: '#C65D52',
        friends: '#7BA38B',
        sports: '#E8C547',
        work: '#5D6D7E',
        family: '#E8B4BC',
        health: '#77A5A8',
        learning: '#4A6583',
        romance: '#C74B50',
        creativity: '#937DC2',
        food: '#D4A574',
        music: '#89B0AE'
    },

    // === FANTASY ===
    lotr: {
        trips: '#C19A6B',
        experiences: '#8B4513',
        friends: '#556B2F',
        sports: '#DAA520',
        work: '#3D2B1F',
        family: '#BC8F8F',
        health: '#228B22',
        learning: '#4A5568',
        romance: '#B22222',
        creativity: '#6B5B95',
        food: '#D2691E',
        music: '#708090'
    },
    gryffindor: {
        trips: '#FF8C00',
        experiences: '#DC143C',
        friends: '#228B22',
        sports: '#FFD700',
        work: '#8B0000',
        family: '#FF69B4',
        health: '#2E8B57',
        learning: '#4169E1',
        romance: '#B22222',
        creativity: '#9400D3',
        food: '#D2691E',
        music: '#4682B4'
    },
    slytherin: {
        trips: '#C0C0C0',
        experiences: '#8B0000',
        friends: '#228B22',
        sports: '#BDB76B',
        work: '#1A472A',
        family: '#DB7093',
        health: '#2E8B57',
        learning: '#4682B4',
        romance: '#B22222',
        creativity: '#6A5ACD',
        food: '#A8A8A8',
        music: '#5F9EA0'
    },
    hufflepuff: {
        trips: '#FFD700',
        experiences: '#DC143C',
        friends: '#32CD32',
        sports: '#FFE135',
        work: '#2F2F2F',
        family: '#FF69B4',
        health: '#3CB371',
        learning: '#4169E1',
        romance: '#FF4500',
        creativity: '#9370DB',
        food: '#FFA500',
        music: '#4682B4'
    },
    ravenclaw: {
        trips: '#CD7F32',
        experiences: '#DC143C',
        friends: '#228B22',
        sports: '#BDB76B',
        work: '#0E1A40',
        family: '#FF69B4',
        health: '#20B2AA',
        learning: '#4169E1',
        romance: '#B22222',
        creativity: '#6A5ACD',
        food: '#DAA520',
        music: '#4682B4'
    },

    // === MINIMAL & MODERN ===
    apple: {
        trips: '#FF9500',
        experiences: '#FF3B30',
        friends: '#34C759',
        sports: '#FFCC00',
        work: '#5856D6',
        family: '#FF2D55',
        health: '#30B0C7',
        learning: '#007AFF',
        romance: '#FF2D55',
        creativity: '#AF52DE',
        food: '#FF9500',
        music: '#5AC8FA'
    },
    calmPastel: {
        trips: '#FFB5A7',
        experiences: '#F28482',
        friends: '#84DCC6',
        sports: '#FFE5A0',
        work: '#9CB4CC',
        family: '#FCD5CE',
        health: '#95D5B2',
        learning: '#A2D2FF',
        romance: '#FFAFCC',
        creativity: '#CDB4DB',
        food: '#FFCDB2',
        music: '#BDE0FE'
    },
    americana: {
        trips: '#B8860B',
        experiences: '#B22222',
        friends: '#2E8B57',
        sports: '#DAA520',
        work: '#1A2744',
        family: '#C71585',
        health: '#228B22',
        learning: '#4169E1',
        romance: '#DC143C',
        creativity: '#6A5ACD',
        food: '#CD853F',
        music: '#4682B4'
    }
};

// Get category colors for a specific theme
export const getCategoryColorsForTheme = (themeName) => {
    return themeCategoryColors[themeName] || defaultCategoryColors;
};

// Get the color for a specific event based on its category and the current theme
// Returns the themed category color if the event has a category, otherwise returns the event's stored color
export const getEventCategoryColor = (event, themeName) => {
    if (!event) return null;

    // If event has a category, use the themed category color
    if (event.category) {
        const themeColors = getCategoryColorsForTheme(themeName);
        if (themeColors[event.category]) {
            return themeColors[event.category];
        }
    }

    // Fallback to stored event color or calendar color
    return event.color || event.calendarColor || null;
};
